import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, CalendarClock } from 'lucide-react';
import { renewalPeriod, planAmount } from '../lib/renewal';

const fmtDate = (d) => new Date(d).toLocaleDateString('es-ES');
const fmtMoney = (n) => Number(n || 0).toFixed(2).replace('.', ',');

/**
 * Renew (or change plan) from an account that already has a plan. Opens with
 * the running plan and its cycle selected, so renewing is two clicks; the
 * client may pick another plan. Shows exactly what the payment buys: the new
 * period starts when the current one ends, and a different plan starts that
 * day too (lib/renewal.js mirrors the backend rule).
 *
 * currentPlanName: the running plan (badge). priorPlanName: the plan in force
 * right before the new period — the same one, unless another plan is already
 * paid for and waiting for its date.
 */
export default function RenewalDialog({
    profile, plans, currentPlanName, priorPlanName = currentPlanName, initialPlanName,
    initialCycle = 'yearly', cur, taxLabel, autoChargeOn, busy, onPay, onClose,
}) {
    const { t } = useTranslation();
    const [planName, setPlanName] = useState(initialPlanName || null);
    const [cycle, setCycle] = useState(initialCycle);

    const plan = plans.find(p => p.name === planName) || null;
    const { start, end, early } = renewalPeriod(profile, cycle);
    const changesPlan = early && plan && priorPlanName && plan.name !== priorPlanName;
    const perCycle = cycle === 'yearly' ? t('subdash.renewPerYear') : t('subdash.renewPerMonth');

    return (
        <div
            className="fixed inset-0 z-[70] bg-[#1d2d3d]/70 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="renewal-dialog-title"
            onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-gray-100">
                    <div>
                        <h2 id="renewal-dialog-title" className="text-xl font-bold text-[#1d2d3d]">{t('subdash.renewTitle')}</h2>
                        <p className="text-sm text-[#6d7d8b] mt-1">
                            {early
                                ? t('subdash.renewIntroEarly', { date: fmtDate(start) })
                                : t('subdash.renewIntroNow')}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        aria-label={t('subdash.renewCancel')}
                        className="text-[#6d7d8b] hover:text-[#1d2d3d] disabled:opacity-50 flex-shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <h3 className="text-xs font-bold text-[#6d7d8b] uppercase tracking-wide mb-2">{t('subdash.renewChoosePlan')}</h3>
                        <div className="space-y-2" role="radiogroup">
                            {plans.map(p => {
                                const selected = p.name === planName;
                                return (
                                    <button
                                        key={p.name}
                                        type="button"
                                        role="radio"
                                        aria-checked={selected}
                                        onClick={() => setPlanName(p.name)}
                                        className={`w-full flex items-center justify-between gap-3 text-left rounded-xl border-2 px-4 py-3 transition-colors ${selected ? 'border-[#1a4f8b] bg-blue-50' : 'border-gray-200 hover:border-blue-200'}`}
                                    >
                                        <span className="flex items-center gap-3 min-w-0">
                                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#1a4f8b] bg-[#1a4f8b]' : 'border-gray-300'}`}>
                                                {selected && <Check className="w-3 h-3 text-white" strokeWidth={4} />}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block font-bold text-[#1d2d3d]">{p.name}</span>
                                                {p.name === currentPlanName && (
                                                    <span className="inline-block mt-0.5 bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                                                        {t('subdash.yourCurrentPlan')}
                                                    </span>
                                                )}
                                            </span>
                                        </span>
                                        <span className="text-sm font-bold text-[#1a4f8b] whitespace-nowrap">
                                            {fmtMoney(planAmount(p, cycle))} {cur}{perCycle}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-xs font-bold text-[#6d7d8b] uppercase tracking-wide mb-2">{t('subdash.renewChooseCycle')}</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {['yearly', 'monthly'].map(c => (
                                <button
                                    key={c}
                                    type="button"
                                    aria-pressed={cycle === c}
                                    onClick={() => setCycle(c)}
                                    className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition-colors ${cycle === c ? 'border-[#1a4f8b] bg-blue-50 text-[#1a4f8b]' : 'border-gray-200 text-[#6d7d8b] hover:border-blue-200'}`}
                                >
                                    {c === 'yearly' ? t('subdash.annual') : t('subdash.monthly')}
                                    {c === 'yearly' && (
                                        <span className="block text-[10px] font-bold text-orange-500 tracking-wide">{t('subdash.save17')}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bg-[#f8fafc] border border-gray-200 rounded-xl p-4 text-sm space-y-2">
                        <div className="flex items-start gap-2 text-[#1d2d3d]">
                            <CalendarClock className="w-4 h-4 text-[#1a4f8b] flex-shrink-0 mt-0.5" />
                            <span>{t('subdash.renewPeriod', { from: fmtDate(start), to: fmtDate(end) })}</span>
                        </div>
                        {changesPlan && (
                            <p className="text-[#6d7d8b]">
                                {t('subdash.renewPlanFrom', { plan: plan.name, date: fmtDate(start), current: priorPlanName })}
                            </p>
                        )}
                        {plan && (
                            <div className="flex items-baseline justify-between gap-3 pt-2 border-t border-gray-200">
                                <span className="font-bold text-[#1d2d3d]">{t('subdash.renewTotal')}</span>
                                <span className="text-right">
                                    <span className="text-lg font-bold text-[#1d2d3d]">{fmtMoney(planAmount(plan, cycle))} {cur}</span>
                                    <span className="block text-[10px] font-bold text-[#6d7d8b]">{t('subdash.taxIncluded', { tax: taxLabel })}</span>
                                </span>
                            </div>
                        )}
                    </div>

                    {autoChargeOn && (
                        <p className="text-xs text-[#6d7d8b]">
                            {t(cycle === 'yearly' ? 'subdash.recurringConsentYearly' : 'subdash.recurringConsentMonthly')}
                        </p>
                    )}
                </div>

                <div className="p-6 pt-0">
                    <div className="flex flex-col-reverse sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={busy}
                            className="sm:w-1/3 py-3 rounded-full font-bold text-[#1d2d3d] bg-gray-100 hover:bg-gray-200 transition-colors text-sm disabled:opacity-60"
                        >
                            {t('subdash.renewCancel')}
                        </button>
                        <button
                            type="button"
                            onClick={() => plan && onPay(plan, cycle)}
                            disabled={!plan || busy}
                            className="flex-1 py-3 rounded-full font-bold text-white bg-[#fca311] hover:bg-orange-500 transition-colors text-sm shadow-md disabled:opacity-60"
                        >
                            {busy ? t('subdash.redirectingToPayment') : t('subdash.renewPay')}
                        </button>
                    </div>
                    <p className="text-center text-[10px] text-gray-400 mt-2">{t('subdash.securePayment')}</p>
                </div>
            </div>
        </div>
    );
}
